import React, { useEffect, useState } from "react";
import {
  makeStyles,
  Paper,
  Grid,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@material-ui/core";
import { Formik, Form, Field } from "formik";
import { Edit as EditIcon } from "@material-ui/icons";
import { toast } from "react-toastify";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";
import useModules from "../../hooks/useModules";
import { i18n } from "../../translate/i18n";


const useStyles = makeStyles((theme) => ({
  mainPaper: {
    width: "100%",
    flex: 1,
    padding: theme.spacing(2),
  },
  fullWidth: {
    width: "100%",
  },
  tableContainer: {
    width: "100%",
    overflowX: "scroll",
    ...theme.scrollbarStyles,
  },
  buttonContainer: {
    textAlign: "right",
    padding: theme.spacing(1),
  },
}));


export function ModuleForm({ onSubmit, onDelete, onCancel, initialValue, loading }) {
  const classes = useStyles();
  const [record, setRecord] = useState({
    key: "",
    name: "",
    description: "",
    category: "core",
    amount: 0,
    isAddon: false,
    isActive: true,
  });

  useEffect(() => {
    setRecord(initialValue);
  }, [initialValue]);

  const handleSubmit = async (data) => {
    onSubmit(data);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={record}
      onSubmit={(values, { resetForm }) =>
        setTimeout(() => {
          handleSubmit(values);
          resetForm();
        }, 300)
      }
    >
      {() => (
        <Form className={classes.fullWidth}>
          <Grid spacing={1} container>
            <Grid xs={12} sm={6} md={2} item>
              <Field
                as={TextField}
                label={i18n.t("modulesManager.table.key")}
                name="key"
                variant="outlined"
                className={classes.fullWidth}
                margin="dense"
              />
            </Grid>
            <Grid xs={12} sm={6} md={3} item>
              <Field
                as={TextField}
                label={i18n.t("common.name")}
                name="name"
                variant="outlined"
                className={classes.fullWidth}
                margin="dense"
              />
            </Grid>
            <Grid xs={12} sm={6} md={2} item>
              <Field
                as={TextField}
                label={i18n.t("modulesManager.table.category")}
                name="category"
                variant="outlined"
                className={classes.fullWidth}
                margin="dense"
              />
            </Grid>
            <Grid xs={12} sm={6} md={2} item>
              <Field
                as={TextField}
                label={i18n.t("common.price")}
                name="amount"
                variant="outlined"
                className={classes.fullWidth}
                margin="dense"
                type="number"
              />
            </Grid>
            <Grid xs={12} sm={6} md={2} item>
              <FormControl margin="dense" variant="outlined" fullWidth>
                <InputLabel htmlFor="addon-selection">{i18n.t("modulesManager.table.addon")}</InputLabel>
                <Field
                  as={Select}
                  id="addon-selection"
                  label={i18n.t("modulesManager.table.addon")}
                  name="isAddon"
                  margin="dense"
                >
                  <MenuItem value={true}>{i18n.t("common.yes")}</MenuItem>
                  <MenuItem value={false}>{i18n.t("common.no")}</MenuItem>
                </Field>
              </FormControl>
            </Grid>
            <Grid xs={12} sm={6} md={2} item>
              <FormControl margin="dense" variant="outlined" fullWidth>
                <InputLabel htmlFor="active-selection">{i18n.t("common.active")}</InputLabel>
                <Field
                  as={Select}
                  id="active-selection"
                  label={i18n.t("common.active")}
                  name="isActive"
                  margin="dense"
                >
                  <MenuItem value={true}>{i18n.t("common.yes")}</MenuItem>
                  <MenuItem value={false}>{i18n.t("common.no")}</MenuItem>
                </Field>
              </FormControl>
            </Grid>
            <Grid xs={12} item>
              <Field
                as={TextField}
                label={i18n.t("common.description")}
                name="description"
                variant="outlined"
                className={classes.fullWidth}
                margin="dense"
                multiline
                rows={2}
              />
            </Grid>
          </Grid>

          <Grid spacing={2} justifyContent="flex-end" container>
            <Grid sm={3} md={2} item>
              <ButtonWithSpinner
                className={classes.fullWidth}
                loading={loading}
                onClick={() => onCancel()}
                variant="contained"
              >
                {i18n.t("common.clear")}
              </ButtonWithSpinner>
            </Grid>
            {record.id !== undefined ? (
              <Grid sm={3} md={2} item>
                <ButtonWithSpinner
                  className={classes.fullWidth}
                  loading={loading}
                  onClick={() => onDelete(record)}
                  variant="contained"
                  color="secondary"
                >
                  {i18n.t("common.deactivate")}
                </ButtonWithSpinner>
              </Grid>
            ) : null}
            <Grid sm={3} md={2} item>
              <ButtonWithSpinner
                className={classes.fullWidth}
                loading={loading}
                type="submit"
                variant="contained"
                color="primary"
              >
                {i18n.t("common.save")}
              </ButtonWithSpinner>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
}


export function ModulesGrid({ records, onSelect }) {
  const classes = useStyles();

  return (
    <Paper className={classes.tableContainer}>
      <Table className={classes.fullWidth} padding="none">
        <TableHead>
          <TableRow>
            <TableCell align="center" style={{ width: "1%" }}>#</TableCell>
            <TableCell>{i18n.t("modulesManager.table.key")}</TableCell>
            <TableCell>{i18n.t("common.name")}</TableCell>
            <TableCell>{i18n.t("modulesManager.table.category")}</TableCell>
            <TableCell align="center">{i18n.t("common.price")}</TableCell>
            <TableCell align="center">{i18n.t("modulesManager.table.addon")}</TableCell>
            <TableCell align="center">{i18n.t("common.active")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map((row) => (
            <TableRow key={row.id}>
              <TableCell align="center" style={{ width: "1%" }}>
                <IconButton onClick={() => onSelect(row)}>
                  <EditIcon />
                </IconButton>
              </TableCell>
              <TableCell>{row.key}</TableCell>
              <TableCell>{row.name}</TableCell>
              <TableCell>{row.category}</TableCell>
              <TableCell align="center">
                {row.amount ? Number(row.amount).toFixed(2) : "0.00"}
              </TableCell>
              <TableCell align="center">{row.is_addon ? i18n.t("common.yes") : i18n.t("common.no")}</TableCell>
              <TableCell align="center">{row.is_active ? i18n.t("common.yes") : i18n.t("common.no")}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}


export default function ModulesManager() {
  const classes = useStyles();
  const { list, save, update, remove } = useModules();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [record, setRecord] = useState({
    key: "",
    name: "",
    description: "",
    category: "core",
    amount: 0,
    isAddon: false,
    isActive: true,
  });

  useEffect(() => {
    loadModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record]);

  const loadModules = async () => {
    setLoading(true);
    try {
      const modules = await list({ active_only: false });
      setRecords(modules);
    } catch (e) {
      toast.error(i18n.t("modulesManager.toasts.loadError"));
    }
    setLoading(false);
  };

  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      if (data.id !== undefined) {
        await update(data);
      } else {
        await save(data);
      }
      await loadModules();
      handleCancel();
      toast.success(i18n.t("modulesManager.toasts.saved"));
    } catch (e) {
      toast.error(i18n.t("modulesManager.toasts.saveError"));
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await remove(record.id);
      await loadModules();
      handleCancel();
      toast.success(i18n.t("modulesManager.toasts.deactivated"));
    } catch (e) {
      toast.error(i18n.t("modulesManager.toasts.deactivateError"));
    }
    setLoading(false);
  };

  const handleCancel = () => {
    setRecord({
      id: undefined,
      key: "",
      name: "",
      description: "",
      category: "core",
      amount: 0,
      isAddon: false,
      isActive: true,
    });
  };

  const handleSelect = (data) => {
    setRecord({
      id: data.id,
      key: data.key || "",
      name: data.name || "",
      description: data.description || "",
      category: data.category || "core",
      amount: data.amount || 0,
      isAddon: data.is_addon === true,
      isActive: data.is_active !== false,
    });
  };

  return (
    <Paper className={classes.mainPaper} elevation={0}>
      <Grid spacing={2} container>
        <Grid xs={12} item>
          <ModuleForm
            initialValue={record}
            onDelete={() => setShowConfirmDialog(true)}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
          />
        </Grid>
        <Grid xs={12} item>
          <ModulesGrid records={records} onSelect={handleSelect} />
        </Grid>
      </Grid>
      <ConfirmationModal
        title={i18n.t("modulesManager.confirm.deactivateTitle")}
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={() => handleDelete()}
      >
        {i18n.t("modulesManager.confirm.deactivateMessage")}
      </ConfirmationModal>
    </Paper>
  );
}
